import { useState } from 'react';
import axios from 'axios';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

import { sorConfigurationUrl, sorOpenAttemptCountsUrl, sorOperationsUrl, sorTestConnectionUrl } from '../api';
import type {
  SorConfiguration,
  SorConfigurationParams,
  SorConfigurationResponse,
  SorConnectionTestResult,
  SorOpenAttemptCounts,
  SorOperation,
  SorOperationsResponse,
} from '../types';

interface UseSorConfigurationReturnType {
  isLoading: boolean;
  isError: boolean;
  configuration: SorConfiguration | null;
  /** Whether the organization's SSO includes offline_access, without which no user has an SSO token for the SoR. */
  isSsoOfflineAccessEnabled: boolean;
  operations: SorOperation[];
  openAttemptCounts: SorOpenAttemptCounts | undefined;
  /** The result of the last connection test run on this screen; the server keeps none. */
  connectionTest: SorConnectionTestResult | null;
  isSaving: boolean;
  isTesting: boolean;
  /** Calls reportAxiosError and rethrows on error */
  saveConfiguration: (params: SorConfigurationParams) => Promise<void>;
  /** Calls reportAxiosError and rethrows on error */
  testConnection: () => Promise<SorConnectionTestResult>;
  /** Resolves to undefined when the counts could not be read; useDataFetcher reports that error */
  reloadOpenAttemptCounts: () => Promise<SorOpenAttemptCounts | undefined>;
}

export const useSorConfiguration = (): UseSorConfigurationReturnType => {
  const { organization } = useSysconfig() as { organization: { id: number } };
  const [connectionTest, setConnectionTest] = useState<SorConnectionTestResult | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const configurationFetcher = useDataFetcher<SorConfigurationResponse>(sorConfigurationUrl(organization.id));
  const operationsFetcher = useDataFetcher<SorOperationsResponse>(sorOperationsUrl(organization.id));
  const openAttemptCountsFetcher = useDataFetcher<SorOpenAttemptCounts>(sorOpenAttemptCountsUrl(organization.id));

  const saveConfiguration = async (params: SorConfigurationParams) => {
    try {
      setIsSaving(true);
      await axios.put(sorConfigurationUrl(organization.id), params);
      await configurationFetcher.reloadData();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      setIsTesting(true);
      const { data } = await axios.post<SorConnectionTestResult>(sorTestConnectionUrl(organization.id));
      setConnectionTest(data);
      return data;
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    } finally {
      setIsTesting(false);
    }
  };

  return {
    isLoading: configurationFetcher.isLoading || operationsFetcher.isLoading || openAttemptCountsFetcher.isLoading,
    isError: configurationFetcher.isError || operationsFetcher.isError || openAttemptCountsFetcher.isError,
    configuration: configurationFetcher.data?.configuration ?? null,
    isSsoOfflineAccessEnabled: configurationFetcher.data?.is_sso_offline_access_enabled ?? false,
    operations: operationsFetcher.data?.operations ?? [],
    openAttemptCounts: openAttemptCountsFetcher.data,
    connectionTest,
    isSaving,
    isTesting,
    saveConfiguration,
    testConnection,
    reloadOpenAttemptCounts: openAttemptCountsFetcher.reloadData,
  };
};
